/**
 * ContasReceberReportPage — relatório PDF de contas a receber (módulo Relatórios).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Search,
  Calendar as CalendarIcon,
  UserRound,
  X,
  CheckCircle,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import AccountsReceivablePdfCanvas from '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas'
import '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas.css'
import { exportElementToPdf } from '../../../../utils/exportElementToPdf'
import { accountReceivableService, customerService } from '../../../../services/erpApi'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { usePermissions, ACTIONS } from '../../../../hooks/usePermissions'
import { canAccessErpReport } from '../../../../utils/permissions'
import './ContasReceberReportPage.css'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'A vencer' },
  { value: 'overdue', label: 'Atrasado' },
  { value: 'paid', label: 'Pago' },
  { value: 'cancelled', label: 'Cancelado' },
]

function ContasReceberReportPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canView = canAccessErpReport(hasPermission, ACTIONS.REPORTS_RECEIVABLES)
  const { toast, showToast, hideToast } = useToast()

  const pdfRef = useRef(null)
  const periodBtnRef = useRef(null)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [exportPdfAccounts, setExportPdfAccounts] = useState([])
  const [previewCount, setPreviewCount] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [modalCustomers, setModalCustomers] = useState([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [draftCustomers, setDraftCustomers] = useState([])

  const [filters, setFilters] = useState({
    search: '',
    status: ['pending', 'overdue'],
    dueDateFrom: '',
    dueDateTo: '',
    nextDays: 7,
    lastDays: null,
  })

  const customerLabel = useMemo(() => {
    if (!selectedCustomers.length) return 'Todos os clientes'
    if (selectedCustomers.length === 1) return selectedCustomers[0].name
    return `${selectedCustomers.length} clientes selecionados`
  }, [selectedCustomers])

  useEffect(() => {
    if (!customerModalOpen) return

    const load = async () => {
      setLoadingCustomers(true)
      try {
        const params = customerSearchTerm.trim()
          ? { search: customerSearchTerm.trim(), status: 'active', limit: 100 }
          : { status: 'active', limit: 1000 }
        const response = await customerService.getAll(params)
        if (response.success) {
          setModalCustomers(response.data || [])
        } else {
          setModalCustomers([])
        }
      } catch (error) {
        console.error('Erro ao buscar clientes:', error)
        setModalCustomers([])
      } finally {
        setLoadingCustomers(false)
      }
    }

    const timeoutId = setTimeout(load, customerSearchTerm.trim() ? 300 : 0)
    return () => clearTimeout(timeoutId)
  }, [customerModalOpen, customerSearchTerm])

  useEffect(() => {
    if (!customerModalOpen) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setCustomerModalOpen(false)
        setCustomerSearchTerm('')
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [customerModalOpen])

  const periodLabel = useMemo(() => {
    if (filters.dueDateFrom && filters.dueDateTo) {
      return `${filters.dueDateFrom} → ${filters.dueDateTo}`
    }
    if (filters.nextDays) return `Próximos ${filters.nextDays} dias`
    if (filters.lastDays) return `Últimos ${filters.lastDays} dias`
    return 'Período'
  }, [filters])

  const toggleStatus = (statusValue) => {
    setFilters((prev) => {
      const current = Array.isArray(prev.status) ? prev.status : []
      const selected = current.includes(statusValue)
        ? current.filter((s) => s !== statusValue)
        : [...current, statusValue]
      return {
        ...prev,
        status: selected.length ? selected : ['pending'],
      }
    })
    setPreviewCount(null)
  }

  const openCustomerModal = () => {
    setDraftCustomers(selectedCustomers)
    setCustomerSearchTerm('')
    setCustomerModalOpen(true)
  }

  const applyCustomerSelection = useCallback((customers) => {
    setSelectedCustomers(customers)
    setPreviewCount(null)
  }, [])

  const toggleDraftCustomer = (customer) => {
    const exists = draftCustomers.some((c) => String(c.id) === String(customer.id))
    const next = exists
      ? draftCustomers.filter((c) => String(c.id) !== String(customer.id))
      : [
          ...draftCustomers,
          {
            id: customer.id,
            name: customer.name,
            document: customer.document || customer.cpf_cnpj || customer.cpfCnpj || null,
          },
        ]
    setDraftCustomers(next)
    // Aplica na hora para o filtro não depender só do Confirmar
    applyCustomerSelection(next)
  }

  const confirmCustomerSelection = () => {
    applyCustomerSelection(draftCustomers)
    setCustomerModalOpen(false)
    setCustomerSearchTerm('')
  }

  const closeCustomerModal = () => {
    // Fecha mantendo o que já foi aplicado ao marcar/desmarcar
    setCustomerModalOpen(false)
    setCustomerSearchTerm('')
  }

  const selectAllCustomers = () => {
    setDraftCustomers([])
    applyCustomerSelection([])
  }

  const buildQuery = useCallback(
    () => ({
      ...filters,
      customerIds: selectedCustomers.map((c) => String(c.id)),
      page: 1,
      limit: 10000,
    }),
    [filters, selectedCustomers]
  )

  const handlePreviewCount = async () => {
    setPreviewLoading(true)
    try {
      const response = await accountReceivableService.getAll(buildQuery())
      if (!response.success) {
        showToast(response.message || 'Erro ao consultar títulos', 'error')
        return
      }
      setPreviewCount(response.data?.length || 0)
    } catch (err) {
      console.error(err)
      showToast('Erro ao consultar títulos', 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleGeneratePdf = async () => {
    setGeneratingPDF(true)
    try {
      const response = await accountReceivableService.getAll(buildQuery())

      if (!response.success || !response.data?.length) {
        showToast('Nenhuma conta encontrada com os filtros atuais', 'warning')
        return
      }

      setExportPdfAccounts(response.data)
      setPreviewCount(response.data.length)
      await new Promise((resolve) => setTimeout(resolve, 200))

      if (!pdfRef.current) {
        throw new Error('Falha ao montar documento PDF')
      }

      const customerSlug =
        selectedCustomers.length === 1
          ? selectedCustomers[0].name.replace(/\s+/g, '_').slice(0, 30)
          : response.data.length === 1 && response.data[0].customer_name
            ? response.data[0].customer_name.replace(/\s+/g, '_').slice(0, 30)
            : 'filtro'
      const filename = `Relatorio_Contas_a_Receber_${customerSlug}_${Date.now()}.pdf`

      await exportElementToPdf(pdfRef.current, filename)
      showToast(`${response.data.length} título(s) exportado(s) em PDF`, 'success')
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
      showToast('Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setExportPdfAccounts([])
    }
  }

  const pdfFilters = useMemo(
    () => ({
      ...filters,
      customerIds: selectedCustomers.map((c) => c.id),
      selectedCustomerNames: selectedCustomers.map((c) => c.name),
    }),
    [filters, selectedCustomers]
  )

  if (!canView) {
    return (
      <div className="cr-report-page">
        <p className="cr-report-denied">Você não tem permissão para este relatório.</p>
      </div>
    )
  }

  return (
    <div className="cr-report-page">
      <div className="cr-report-header">
        <div className="cr-report-header-left">
          <button
            type="button"
            className="cr-report-back"
            onClick={() => navigate('/erp/relatorios')}
            aria-label="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="cr-report-title">Contas a Receber</h1>
            <p className="cr-report-subtitle">Defina os filtros e gere o PDF</p>
          </div>
        </div>
        <button
          type="button"
          className="cr-report-btn-primary"
          onClick={handleGeneratePdf}
          disabled={generatingPDF}
        >
          <Download size={16} />
          {generatingPDF ? 'Gerando…' : 'Gerar PDF'}
        </button>
      </div>

      <div className="cr-report-card">
        <h2 className="cr-report-section-title">Filtros</h2>

        <div className="cr-report-field">
          <label htmlFor="cr-search">Busca</label>
          <div className="cr-report-input-wrap">
            <Search size={16} className="cr-report-input-icon" />
            <input
              id="cr-search"
              type="text"
              placeholder="Cliente, descrição…"
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, search: e.target.value }))
                setPreviewCount(null)
              }}
            />
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Status</span>
          <div className="cr-report-chips">
            {STATUS_OPTIONS.map((opt) => {
              const active = filters.status.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`cr-report-chip${active ? ' is-active' : ''}`}
                  onClick={() => toggleStatus(opt.value)}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Cliente</span>
          <button
            type="button"
            className={`cr-report-customer-trigger${selectedCustomers.length ? ' has-value' : ''}`}
            onClick={openCustomerModal}
          >
            <UserRound size={16} className="cr-report-customer-trigger-icon" />
            <span className="cr-report-customer-trigger-text">{customerLabel}</span>
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Período de vencimento</span>
          <div className="cr-report-period-row">
            <button
              type="button"
              ref={periodBtnRef}
              className="cr-report-period-btn"
              onClick={() => setIsPeriodOpen((v) => !v)}
            >
              <CalendarIcon size={16} />
              {periodLabel}
            </button>
            <div className="cr-report-presets">
              {[7, 15, 30].map((days) => (
                <button
                  key={days}
                  type="button"
                  className={`cr-report-chip${filters.nextDays === days && !filters.dueDateFrom ? ' is-active' : ''}`}
                  onClick={() => {
                    setFilters((prev) => ({
                      ...prev,
                      nextDays: days,
                      lastDays: null,
                      dueDateFrom: '',
                      dueDateTo: '',
                    }))
                    setPreviewCount(null)
                  }}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dueDateFrom}
            endValue={filters.dueDateTo}
            onRangeChange={(start, end) => {
              let from = start
              let to = end
              if (from && to && from > to) {
                ;[from, to] = [to, from]
              }
              setFilters((prev) => ({
                ...prev,
                dueDateFrom: from || '',
                dueDateTo: to || '',
                nextDays: null,
                lastDays: null,
              }))
              setPreviewCount(null)
            }}
            onClear={() => {
              setFilters((prev) => ({
                ...prev,
                dueDateFrom: '',
                dueDateTo: '',
                nextDays: 7,
                lastDays: null,
              }))
              setPreviewCount(null)
            }}
            title="Vencimento"
            subtitle="Início e Fim"
          />
        </div>

        <div className="cr-report-actions">
          <button
            type="button"
            className="cr-report-btn-secondary"
            onClick={handlePreviewCount}
            disabled={previewLoading || generatingPDF}
          >
            {previewLoading ? 'Consultando…' : 'Contar títulos'}
          </button>
          {previewCount != null && (
            <span className="cr-report-count">
              {previewCount} título{previewCount === 1 ? '' : 's'} com os filtros atuais
            </span>
          )}
        </div>
      </div>

      {customerModalOpen && (
        <div className="cr-report-modal-overlay" onClick={closeCustomerModal}>
          <div
            className="cr-report-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cr-customer-modal-title"
          >
            <div className="cr-report-modal-header">
              <div className="cr-report-modal-header-left">
                <div className="cr-report-modal-icon">
                  <UserRound size={24} />
                </div>
                <div>
                  <h3 id="cr-customer-modal-title" className="cr-report-modal-title">
                    Selecionar Cliente
                  </h3>
                  <p className="cr-report-modal-subtitle">
                    Um, vários ou todos os clientes
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeCustomerModal}
                className="cr-report-modal-close"
                aria-label="Fechar"
              >
                <X size={24} />
              </button>
            </div>

            <div className="cr-report-modal-body">
              <div className="cr-report-search-box">
                <Search size={18} className="cr-report-search-icon" />
                <input
                  type="text"
                  className="cr-report-search-input"
                  placeholder="Buscar por nome ou documento..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingCustomers && (
                  <div className="cr-report-search-loading">
                    <Loader2 size={16} className="cr-report-spinning" />
                  </div>
                )}
              </div>

              <button
                type="button"
                className={`cr-report-select-item cr-report-select-all${draftCustomers.length === 0 ? ' selected' : ''}`}
                onClick={selectAllCustomers}
              >
                <div className="cr-report-select-item-avatar">
                  <UserRound size={20} />
                </div>
                <div className="cr-report-select-item-info">
                  <div className="cr-report-select-item-name">Todos os clientes</div>
                  <div className="cr-report-select-item-detail">
                    Inclui títulos de qualquer cliente
                  </div>
                </div>
                {draftCustomers.length === 0 && (
                  <CheckCircle size={20} className="cr-report-select-item-check" />
                )}
              </button>

              <div className="cr-report-select-list">
                {loadingCustomers ? (
                  <div className="cr-report-select-empty">
                    <Loader2 size={32} className="cr-report-spinning" />
                    <p>Buscando clientes...</p>
                  </div>
                ) : modalCustomers.length > 0 ? (
                  modalCustomers.map((customer) => {
                    const selected = draftCustomers.some(
                      (c) => String(c.id) === String(customer.id)
                    )
                    return (
                      <button
                        type="button"
                        key={customer.id}
                        className={`cr-report-select-item${selected ? ' selected' : ''}`}
                        onClick={() => toggleDraftCustomer(customer)}
                      >
                        <div className="cr-report-select-item-avatar">
                          <UserRound size={20} />
                        </div>
                        <div className="cr-report-select-item-info">
                          <div className="cr-report-select-item-name">{customer.name}</div>
                          {(customer.document || customer.cpf_cnpj || customer.cpfCnpj) && (
                            <div className="cr-report-select-item-detail">
                              {customer.document || customer.cpf_cnpj || customer.cpfCnpj}
                            </div>
                          )}
                        </div>
                        {selected && (
                          <CheckCircle size={20} className="cr-report-select-item-check" />
                        )}
                      </button>
                    )
                  })
                ) : (
                  <div className="cr-report-select-empty">
                    <UserRound size={48} />
                    <p>
                      {customerSearchTerm.trim()
                        ? 'Nenhum cliente encontrado'
                        : 'Nenhum cliente disponível'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="cr-report-modal-footer">
              <button
                type="button"
                className="cr-report-modal-btn-secondary"
                onClick={closeCustomerModal}
              >
                Fechar
              </button>
              <button
                type="button"
                className="cr-report-modal-btn-primary"
                onClick={confirmCustomerSelection}
              >
                {draftCustomers.length === 0
                  ? 'Usar todos'
                  : `Confirmar (${draftCustomers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={hideToast} />

      {generatingPDF && (
        <div className="ar-pdf-overlay">
          <div className="ar-pdf-overlay-card">
            <Download size={28} color="#10b981" />
            <h4>Gerando PDF</h4>
            <p>Preparando relatório de contas a receber...</p>
          </div>
        </div>
      )}

      {exportPdfAccounts.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          <AccountsReceivablePdfCanvas
            ref={pdfRef}
            accounts={exportPdfAccounts}
            filters={pdfFilters}
          />
        </div>
      )}
    </div>
  )
}

export default ContasReceberReportPage
